import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaTrash, FaEye, FaSearch, FaBriefcase, FaBuilding, FaMapMarkerAlt, FaUsers, FaShareAlt } from 'react-icons/fa';
import '../pages_premium.css';
import { getFrontendBaseUrl } from '../utils/url';
import AdminShareModal from '../components/common/AdminShareModal';
import AdminPagination from '../components/common/AdminPagination';

function HiremateJobsList() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [shareItem, setShareItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const navigate = useNavigate();

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const handleShareJob = (job) => {
        const frontendUrl = getFrontendBaseUrl();
        setShareItem({
            url: `${frontendUrl}/hiremate/${job.id}`,
            title: job.title || job.role || 'Job Posting',
            category: 'Hiremate Job',
            price: job.budget || (job.hourly_rate ? `$${job.hourly_rate}/hr` : (job.pay_rate ? `$${job.pay_rate}/hr` : '')),
            location: job.location || job.work_address || '',
            description: job.description || job.job_description || ''
        });
    };

    const fetchJobs = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/admin/hiremate-jobs');
            setJobs(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error('Error fetching hiremate jobs:', error);
            toast.error('Failed to fetch posted jobs');
            setJobs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs();
    }, []);

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this job posting?")) {
            try {
                await axiosInstance.delete(`/admin/hiremate-jobs/${id}`);
                toast.success('Job posting deleted successfully');
                fetchJobs();
            } catch (error) {
                console.error('Error deleting job posting:', error);
                toast.error('Failed to delete job posting');
            }
        }
    };

    const parseJSON = (val, fallback = []) => {
        if (!val) return fallback;
        if (typeof val === 'object') return val;
        try {
            return JSON.parse(val);
        } catch (e) {
            return fallback;
        }
    };

    const formatDateTimeSingleLine = (job) => {
        let dateStr = '';
        if (job.date_type === 'flexible') {
            dateStr = 'Flexible Date';
        } else if (job.task_date) {
            dateStr = job.task_date;
            if (job.date_type === 'before_date') {
                dateStr = `Before ${dateStr}`;
            }
        } else {
            dateStr = 'Date specified upon hire';
        }

        let timeStr = '';
        if (job.time_of_day) {
            const times = parseJSON(job.time_of_day, job.time_of_day);
            timeStr = Array.isArray(times) ? times.join(', ') : String(times);
        }

        if (dateStr && timeStr) {
            return `${dateStr} • ${timeStr}`;
        }
        return dateStr || timeStr || 'Flexible';
    };

    const filtered = jobs.filter(job => {
        const title = job.title || '';
        const posterName = job.Poster?.name || '';
        const company = job.company_name || '';
        const suburb = job.suburb || '';
        const query = search.toLowerCase();
        return title.toLowerCase().includes(query) ||
            posterName.toLowerCase().includes(query) ||
            company.toLowerCase().includes(query) ||
            suburb.toLowerCase().includes(query);
    });

    const paginatedJobs = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <div className="page-premium-wrapper hiremate-jobs-list-page">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties">
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaBriefcase size={26} />
                        </div>
                        <div>
                            <h1 className="ph-title">Hiremate Jobs</h1>
                            <p className="ph-subtitle">Monitor and manage all job postings created by Business Owners and Users</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{jobs.length}</span>
                            <span className="count-lbl">Total Jobs</span>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            Posted Jobs List
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by job title, poster, company or location..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        {loading ? (
                            <div className="text-center py-5 text-secondary">Loading job postings...</div>
                        ) : (
                            <table className="premium-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '50px' }}>#</th>
                                        <th style={{ minWidth: '220px' }}>Job / Task Title</th>
                                        <th style={{ minWidth: '180px' }}>Posted By</th>
                                        <th style={{ minWidth: '180px' }}>Company Name</th>
                                        <th style={{ minWidth: '130px' }}>Budget / Pay</th>
                                        <th style={{ minWidth: '220px', whiteSpace: 'nowrap' }}>Scheduled Date & Time</th>
                                        <th style={{ minWidth: '120px', textAlign: 'center' }}>Total Requests</th>
                                        <th style={{ textAlign: 'center', width: '220px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedJobs.map((job, index) => {
                                        const poster = job.Poster || {};
                                        const isHourly = job.budget_type === 'hourly';

                                        return (
                                            <tr key={job.id}>
                                                <td style={{ color: '#9ba6c0', fontWeight: 600 }}>#{(currentPage - 1) * pageSize + index + 1}</td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <strong style={{ color: '#0f172a', fontSize: '0.9rem' }}>{job.title}</strong>
                                                        <span style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                                                            <FaMapMarkerAlt size={10} style={{ color: '#0ab68e' }} />
                                                            {job.location_type === 'online' ? 'Remote / Online' : (job.suburb || 'In Person')}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <strong style={{ color: '#0f172a', fontSize: '0.88rem', whiteSpace: 'nowrap' }}>{poster.name || 'Unknown User'}</strong>
                                                        <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>{poster.email || '-'}</span>
                                                        <span style={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{poster.phone || '-'}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <FaBuilding style={{ color: '#0ab68e', flexShrink: 0 }} />
                                                        <strong style={{ color: '#334155', fontSize: '0.88rem', whiteSpace: 'nowrap' }}>{job.company_name || 'Individual'}</strong>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span style={{
                                                        backgroundColor: '#f1f5f9',
                                                        color: '#0f172a',
                                                        padding: '4px 10px',
                                                        borderRadius: '12px',
                                                        fontWeight: '700',
                                                        fontSize: '12px',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        ${parseFloat(job.budget || 0).toFixed(2)} {isHourly ? '/ hr' : '(Total)'}
                                                    </span>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <span style={{ fontSize: '13px', color: '#334155', fontWeight: '500', whiteSpace: 'nowrap' }}>
                                                        {formatDateTimeSingleLine(job)}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                    <span style={{
                                                        backgroundColor: '#e0f2fe',
                                                        color: '#0369a1',
                                                        padding: '4px 10px',
                                                        borderRadius: '12px',
                                                        fontWeight: '700',
                                                        fontSize: '12px'
                                                    }}>
                                                        {job.total_requests || 0} Requests
                                                    </span>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <div className="hire-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                                                        <button
                                                            onClick={() => navigate(`/hiremate-jobs/${job.id}/requests`)}
                                                            style={{
                                                                backgroundColor: '#4f46e5',
                                                                color: '#ffffff',
                                                                border: 'none',
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: '600',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '6px',
                                                                whiteSpace: 'nowrap',
                                                                boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)'
                                                            }}
                                                        >
                                                            <FaUsers style={{ fontSize: '13px' }} /> View Requests
                                                        </button>
                                                        <button
                                                            onClick={() => handleShareJob(job)}
                                                            className="action-btn action-btn-share"
                                                            data-tooltip="Share Job"
                                                            title="Share Job"
                                                            style={{ backgroundColor: '#ef4444', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer' }}
                                                        >
                                                            <FaShareAlt />
                                                        </button>
                                                        <button
                                                            onClick={() => navigate(`/hiremate-jobs/${job.id}`)}
                                                            className="action-btn action-btn-view"
                                                            data-tooltip="View Job Details"
                                                            style={{ backgroundColor: '#0ab68e', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer' }}
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(job.id)}
                                                            className="action-btn action-btn-delete"
                                                            data-tooltip="Delete Job"
                                                            style={{ backgroundColor: '#ef4444', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer' }}
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {filtered.length === 0 && (
                                        <tr>
                                            <td colSpan="8">
                                                <div className="ptable-empty">
                                                    <div className="ptable-empty-icon">💼</div>
                                                    <p>No job postings found.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <AdminPagination
                        currentPage={currentPage}
                        totalItems={filtered.length}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                        itemLabel="jobs"
                    />
                </div>

            </div>

            <AdminShareModal
                isOpen={!!shareItem}
                onClose={() => setShareItem(null)}
                {...shareItem}
            />
        </div>
    );
}

export default HiremateJobsList;
